import type {
  LeaderboardEntry,
  PeriodLeaderboards,
  ServerSnapshotMessage,
} from "@xiuxian/game-protocol";
import {
  formatDefaultPlayerName,
  formatPeriodLabel,
  formatPeriodTitle,
  formatRealmLayer,
  formatRealmLayerCompact,
  formatScoreValue,
  type PeriodLeaderboardKind,
} from "./i18n/game-formatters.js";
import { DEFAULT_LOCALE, type Locale } from "./i18n/index.js";
import { getTreasureUiConfig } from "./treasure-ui.js";

export type { PeriodLeaderboardKind } from "./i18n/game-formatters.js";

export interface LeaderboardRenderState {
  snapshot: ServerSnapshotMessage | null;
  activeLeaderboard: LeaderboardEntry[];
  periodLeaderboards: PeriodLeaderboards | null;
  selectedPeriod: PeriodLeaderboardKind;
  showPeriodTabs: boolean;
  statusMessage: string;
  currentPlayerKey: string | null;
  currentPlayerName: string;
  locale?: Locale;
}

export function renderLeaderboardPanel(state: LeaderboardRenderState): string {
  const locale = state.locale ?? DEFAULT_LOCALE;
  const activeLeaderboard = renderActiveLeaderboard(state);
  const periodLeaderboard = renderPeriodLeaderboard(state);
  const shouldRenderPeriodSection =
    !!periodLeaderboard ||
    (state.showPeriodTabs && hasAnyPeriodLeaderboardData(state));
  return [
    activeLeaderboard
      ? [
          `<section class="leaderboard-section">`,
          activeLeaderboard,
          `</section>`,
        ].join("")
      : "",
    shouldRenderPeriodSection
      ? [
          `<section class="leaderboard-section period-board">`,
          `<div class="period-title">${formatPeriodTitle(locale, state.selectedPeriod)}</div>`,
          state.showPeriodTabs
            ? renderPeriodTabs(state.selectedPeriod, locale)
            : "",
          periodLeaderboard,
          `</section>`,
        ].join("")
      : "",
  ].join("");
}

function hasAnyPeriodLeaderboardData(state: LeaderboardRenderState): boolean {
  const leaderboards = state.periodLeaderboards;
  return !!(
    leaderboards &&
    (leaderboards.day.length > 0 ||
      leaderboards.week.length > 0 ||
      leaderboards.month.length > 0)
  );
}

export function renderActiveLeaderboard(state: LeaderboardRenderState): string {
  const snapshot = state.snapshot;
  const locale = state.locale ?? DEFAULT_LOCALE;
  if (!snapshot || state.activeLeaderboard.length === 0) {
    return "";
  }

  return (
    state.activeLeaderboard
      // Drop the self row when the player is not actually on the board (rank > 6);
      // their level/score is shown separately beside the 天榜 instead.
      .filter((player) => !(player.id === snapshot.self.id && player.rank > 6))
      .map((player) => {
        const isSelf = player.id === snapshot.self.id;
        const rankLabel =
          isSelf && player.rank > 6 ? "" : player.rank.toString();
        const className = [
          "leaderboard-row",
          isSelf ? "is-self" : "",
          player.isAlive ? "" : "is-dead",
        ]
          .filter(Boolean)
          .join(" ");
        const compactLevel = formatLeaderboardLevelBadge(locale, player.level);
        const fullLevel = formatRealmLayer(locale, player.level);
        const nameClass = [
          "leaderboard-name",
          player.teamId ? `is-team-${player.teamId}` : "",
        ]
          .filter(Boolean)
          .join(" ");
        return [
          `<div class="${className}">`,
          `<span class="leaderboard-rank">${rankLabel}</span>`,
          `<span class="${nameClass}">${escapeHtml(displayPlayerName(player.name, locale))}${renderSpectatorCount(player.spectatorCount ?? 0)}</span>`,
          renderTreasureIcons(player, locale),
          `<span class="leaderboard-level" title="${escapeHtml(fullLevel)}" aria-label="${escapeHtml(fullLevel)}">${escapeHtml(compactLevel)}</span>`,
          `<span class="leaderboard-score">${formatScore(player.score ?? 0, locale)}</span>`,
          "</div>",
        ].join("");
      })
      .join("")
  );
}

function renderSpectatorCount(count: number): string {
  if (count <= 0) {
    return "";
  }
  return `<span class="leaderboard-spectators" title="观战人数" aria-label="观战人数 ${count.toString()}">👁 ${count.toString()}</span>`;
}

function renderTreasureIcons(player: LeaderboardEntry, locale: Locale): string {
  const kinds =
    player.treasureKinds && player.treasureKinds.length > 0
      ? player.treasureKinds
      : player.treasureKind
        ? [player.treasureKind]
        : [];
  if (kinds.length === 0) {
    return `<span class="leaderboard-treasure-icon is-empty" aria-hidden="true"></span>`;
  }

  return `<span class="leaderboard-treasure-icons">${kinds
    .map((kind) => {
      const config = getTreasureUiConfig(kind, locale);
      return `<img class="leaderboard-treasure-icon" src="${config.iconPath}" alt="" title="${escapeHtml(config.name)}" aria-label="${escapeHtml(config.name)}">`;
    })
    .join("")}</span>`;
}

export function renderPeriodLeaderboard(state: LeaderboardRenderState): string {
  const entries = state.periodLeaderboards?.[state.selectedPeriod] ?? [];
  const locale = state.locale ?? DEFAULT_LOCALE;
  if (entries.length === 0) {
    return "";
  }

  return entries
    .map((entry) => {
      const isSelf = isCurrentPeriodEntry(state, entry);
      return [
        `<div class="period-leaderboard-row${entry.isActive ? " is-active" : ""}${isSelf ? " is-self" : ""}">`,
        `<span class="leaderboard-rank">${entry.rank.toString()}</span>`,
        `<span class="leaderboard-name">${escapeHtml(displayPlayerName(entry.name, locale))}</span>`,
        `<span class="leaderboard-score">${formatScore(entry.score, locale)}</span>`,
        `</div>`,
      ].join("");
    })
    .join("");
}

export function renderPeriodTabs(
  selectedPeriod: PeriodLeaderboardKind,
  locale: Locale = DEFAULT_LOCALE,
): string {
  return `<div class="period-tabs" role="tablist">${(
    ["day", "week", "month"] as PeriodLeaderboardKind[]
  )
    .map(
      (period) =>
        `<button class="period-tab${period === selectedPeriod ? " is-active" : ""}" type="button" data-period="${period}" role="tab" aria-selected="${period === selectedPeriod ? "true" : "false"}">${formatPeriodLabel(locale, period)}</button>`,
    )
    .join("")}</div>`;
}

export function formatScore(
  value: number,
  locale: Locale = DEFAULT_LOCALE,
): string {
  return formatScoreValue(locale, value);
}

function isCurrentPeriodEntry(
  state: LeaderboardRenderState,
  entry: PeriodLeaderboards[PeriodLeaderboardKind][number],
): boolean {
  return (
    !!state.currentPlayerKey &&
    entry.playerKey === state.currentPlayerKey &&
    displayPlayerName(entry.name, state.locale ?? DEFAULT_LOCALE) ===
      displayPlayerName(state.currentPlayerName, state.locale ?? DEFAULT_LOCALE)
  );
}

function displayPlayerName(
  playerName: string,
  locale: Locale = DEFAULT_LOCALE,
): string {
  const trimmed = playerName.trim();
  return trimmed.length > 0 ? trimmed : formatDefaultPlayerName(locale);
}

function formatLeaderboardLevelBadge(locale: Locale, level: number): string {
  const compact = formatRealmLayerCompact(locale, level);
  const normalizedLevel = Math.max(1, Math.min(50, Math.floor(level)));
  const layer = ((normalizedLevel - 1) % 10) + 1;
  if (locale === "en") {
    return `${compact.trim().charAt(0).toUpperCase()}${layer.toString()}`;
  }
  return compact
    .replace(/\s+/g, "")
    .replace(new RegExp(`${layer}层$`), layer.toString());
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}
